/**
 * RunQaInspectorPanel — "would this run have needed a human, and on what evidence"
 * (PROD-QA-003, MASTER_FLOW §5A.4).
 *
 * Read-only, and every conclusion on it comes from one author: GET /api/agent-runs/:id/qa-inspection
 * (app/services/run_qa_inspector.py) — the verdict arm, its reason, the findings, the decision inputs
 * with their provenance, the evidence that is missing, and the closure judgement. The panel derives
 * nothing: it does not re-grade a finding, does not re-derive closure eligibility from the slots, and
 * does not read `run.analysis`, `run.context` or the action timeline itself. A panel that recomputed
 * any of that would be a second policy, and the two would eventually disagree on the screen.
 *
 * It also grants nothing. There is no approve, reject, close, retry or resume control here, and the
 * projection's `authority` block is rendered as a notice rather than consumed as a permission.
 *
 * Loading, unavailable and "no run" are three separate arms and none of them falls back to a local
 * guess: an inspection that cannot be read is reported as unread, never as "looks fine".
 */
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi } from "../services/api";
import { runStatusLabel } from "./runStatusVocabulary";
import {
  QA_ABSENT,
  QA_SLOT_FIELD_UNKNOWN_VALUE,
  qaAuthorityNotice,
  qaClosureReasonText,
  qaFieldKnown,
  qaFieldLabel,
  qaFieldValue,
  qaFindingMeta,
  qaSlotLabel,
  qaSlotStatusMeta,
  qaVerdictMeta,
  qaVerdictReasonText,
} from "./runQaVocabulary";

function txt(value, max = 160) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function asArray(value) {
  return Array.isArray(value) ? value.filter((item) => item && typeof item === "object") : [];
}

function Ltr({ children }) {
  return <span dir="ltr" className="font-mono">{children}</span>;
}

// One slot field. `mono` is the vocabulary's decision, not this component's: an identifier and a
// producer's own message are echoed left-to-right, everything else is a worded Hebrew value.
//
// A field this build has no label for renders its presence and NOT its value. The server's own test
// proves every field it emits today has a label, so this is the fail-closed arm for a field added
// later: an unnamed field is one whose shape this build cannot vouch for, and printing it would be
// how a raw payload — a token, a credential — reaches the screen unchallenged.
function FieldRow({ slotKind, fieldKey, value }) {
  const known = qaFieldKnown(slotKind, fieldKey);
  const { text, mono } = qaFieldValue(slotKind, fieldKey, value);
  return (
    <div className="flex items-start gap-1.5 text-xs">
      <span className="text-gray-500 shrink-0">{qaFieldLabel(slotKind, fieldKey)}:</span>
      <span className="text-gray-800 min-w-0 break-words">
        {known ? (mono ? <Ltr>{text}</Ltr> : text) : QA_SLOT_FIELD_UNKNOWN_VALUE}
      </span>
    </div>
  );
}

function SlotRow({ entry }) {
  const kind = txt(entry.kind, 60) || "unknown";
  const status = qaSlotStatusMeta(entry.status);
  const value = asObject(entry.value);
  const detail = asObject(entry.detail);
  const sources = Array.isArray(entry.sources)
    ? entry.sources.filter((s) => typeof s === "string" && s.trim())
    : [];
  const fields = value ? Object.keys(value) : [];
  const details = detail ? Object.keys(detail) : [];

  return (
    <div data-testid={`rqi-slot-${kind}`} className="flex items-start gap-2 text-sm">
      <span className="text-gray-500 shrink-0 w-36">{qaSlotLabel(entry.kind)}</span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className={`text-[10px] px-1.5 py-0.5 rounded-full border inline-block ${status.cls}`}>
          {status.label}
        </div>
        {fields.map((key) => (
          <FieldRow key={key} slotKind={entry.kind} fieldKey={key} value={value[key]} />
        ))}
        {details.map((key) => (
          <FieldRow key={`d-${key}`} slotKind={entry.kind} fieldKey={key} value={detail[key]} />
        ))}
        {sources.length > 0 && (
          <div dir="ltr" className="text-[10px] text-gray-400 font-mono break-all">{sources.join(" · ")}</div>
        )}
      </div>
    </div>
  );
}

function FindingRow({ finding }) {
  const meta = qaFindingMeta(finding);
  const code = txt(finding.code, 80);
  return (
    <li className="text-xs">
      <span className="font-medium text-gray-800">{meta.title}</span>
      <span className="text-gray-600"> — {meta.detail}</span>
      {/* An unworded code is echoed so the operator can see WHAT was reported even when this build
          cannot say what it means. Only unknown codes are echoed: for a known one the title above
          already names it, and printing the token too would be a raw identifier on a Hebrew row. */}
      {meta.unknown && code ? (
        <span className="ms-1 text-[10px] text-gray-500"><Ltr>{code}</Ltr></span>
      ) : null}
    </li>
  );
}

export default function RunQaInspectorPanel({ runId }) {
  const id = txt(runId, 80);
  const query = useQuery({
    queryKey: ["run-qa-inspection", id],
    queryFn: () => agentRunsApi.qaInspection(id),
    enabled: Boolean(id),
    retry: false,
  });

  const projection = asObject(query.data);
  const arm = projection ? "projection"
    : query.isError ? "unavailable"
      : query.isLoading ? "loading"
        : "norun";

  const verdictMeta = arm === "projection" ? qaVerdictMeta(projection.verdict) : null;
  const verdictDetail = arm === "projection" ? asObject(projection.verdict_detail) : null;
  const closure = arm === "projection" ? asObject(projection.closure) : null;
  const closureDetail = closure ? asObject(closure.detail) : null;
  const findings = arm === "projection" ? asArray(projection.findings) : [];
  const slots = arm === "projection" ? asArray(projection.decision_inputs) : [];
  const missing = arm === "projection" ? asArray(projection.missing_evidence) : [];
  const authority = arm === "projection" ? qaAuthorityNotice(projection.authority) : null;

  // The verdict's own `run_status` is the status the server classified. It is worded by the shared
  // run-status vocabulary so it reads identically to the status chip elsewhere on this page.
  const classifiedStatus = txt(verdictDetail?.run_status, 40);

  return (
    <section
      data-testid="run-qa-inspector"
      aria-labelledby="run-qa-title"
      className="bg-white rounded-xl shadow-sm border p-4 space-y-3"
      dir="rtl"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="run-qa-title" className="text-sm font-semibold text-gray-700">בקרת איכות הריצה</h2>
        {arm === "projection" ? (
          <span
            data-testid="rqi-verdict"
            className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${verdictMeta.cls}`}
          >
            {verdictMeta.label}
          </span>
        ) : (
          <span
            data-testid="rqi-verdict"
            className="text-[11px] px-2 py-0.5 rounded-full border font-medium bg-gray-200 text-gray-600 border-gray-300"
          >
            {arm === "loading" ? "בודק את הריצה…" : arm === "unavailable" ? "לא ניתן לקרוא" : "אין ריצה לבדוק"}
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-400">
        קלטי ההחלטה של הריצה, המקור של כל אחד מהם, הממצאים שהם תומכים בהם והראיות שחסרות — כפי שהשרת
        משליך אותם מהרשומות. המשטח אינו מסיק סיווג בעצמו ואינו נותן הרצה, אישור או שינוי בקריאה.
      </p>

      {arm === "norun" && (
        <div role="status" className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-3 py-2">
          אין ריצה לבדוק — הפאנל מוצג רק בתוך ריצה קיימת.
        </div>
      )}
      {arm === "unavailable" && (
        <div role="status" className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-3 py-2">
          לא ניתן לקרוא את בקרת האיכות של הריצה מהשרת. הפאנל אינו מסיק סיווג בעצמו, ולכן אינו מציג כאן
          ממצא, סיווג או כשירות לסגירה שאינם מאומתים.
        </div>
      )}
      {arm === "loading" && <p className="text-xs text-gray-400">טוען את בקרת האיכות…</p>}

      {arm === "projection" && (
        <>
          <div
            role={verdictMeta.alarm ? "alert" : "status"}
            className={`text-xs rounded px-3 py-2 border ${verdictMeta.cls}`}
          >
            <div className="font-medium">{verdictMeta.label}</div>
            <div>{qaVerdictReasonText(projection.verdict_reason)}</div>
            <div className="text-[11px] opacity-80">{verdictMeta.hint}</div>
            {classifiedStatus && (
              <div className="text-[11px] opacity-80">
                מצב הריצה שנבדק: <span className="font-medium">{runStatusLabel(classifiedStatus)}</span>
              </div>
            )}
          </div>

          {closure && (
            <div className="text-xs border-t border-gray-100 pt-2">
              <span className="font-medium text-gray-700">
                {closure.eligible ? "ניתן לסגור ללא קריאה חוזרת" : "לא ניתן לסגור ללא קריאה חוזרת"}
              </span>
              <span className="text-gray-600"> — {qaClosureReasonText(closure.reason)}</span>
              {Array.isArray(closureDetail?.missing_slots) && closureDetail.missing_slots.length > 0 && (
                <div className="text-gray-600">
                  חסרים: {closureDetail.missing_slots.map((kind) => qaSlotLabel(kind)).join(" · ")}
                </div>
              )}
            </div>
          )}

          {findings.length > 0 ? (
            <div>
              <div className="text-xs font-semibold text-gray-700">
                ממצאים ({findings.length})
              </div>
              <ul data-testid="rqi-findings" className="list-disc ps-5 space-y-0.5">
                {findings.map((finding, index) => (
                  <FindingRow key={`${txt(finding.code, 60) || "finding"}-${index}`} finding={finding} />
                ))}
              </ul>
            </div>
          ) : (
            <div data-testid="rqi-no-findings" className="text-xs bg-green-50 border border-green-200 text-green-800 rounded px-3 py-2">
              לא סומן אף ממצא — כל בדיקות הראיות עברו.
            </div>
          )}

          {slots.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-700">קלטי ההחלטה</div>
              <div data-testid="rqi-slots" className="space-y-1.5">
                {slots.map((entry, index) => (
                  <SlotRow key={`${txt(entry.kind, 60) || "slot"}-${index}`} entry={entry} />
                ))}
              </div>
            </div>
          )}

          {missing.length > 0 && (
            <div
              data-testid="rqi-missing"
              className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2"
            >
              <div className="font-medium">ראיות חסרות</div>
              <ul className="list-disc ps-5">
                {missing.map((item, index) => {
                  const status = qaSlotStatusMeta(item.status);
                  return (
                    <li key={`${txt(item.kind, 60) || "missing"}-${index}`}>
                      {qaSlotLabel(item.kind)} — {status.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {authority && (
            <p
              data-testid="rqi-authority"
              className={`text-[11px] border-t border-gray-100 pt-2 ${
                authority.alarm ? "text-red-700 font-medium" : "text-gray-400"
              }`}
            >
              {authority.text}
            </p>
          )}

          <p className="text-[10px] text-gray-400">
            נבדק בתאריך <Ltr>{txt(projection.generated_at, 40) || QA_ABSENT}</Ltr>
            {" · "}
            גרסת סכימה <Ltr>{txt(projection.schema_version, 20) || QA_ABSENT}</Ltr>
          </p>
        </>
      )}
    </section>
  );
}
